// The one paywall. Shown at natural friction points only (4th AI photo of the
// day, locked weekly check-in) — never as a launch interstitial. Boringly
// honest billing: full price + trial terms up front, obvious restore, and the
// native side never mentions web pricing (App Review 3.1.1).
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useT, useLocale } from "../lib/i18n";
import { PRICING } from "@shared/pricing";
import { formatPrice } from "../lib/price";
import { useToast } from "../lib/toast";
import { isNativeApp } from "../lib/native";
import { getPriceInfo, purchase, restorePurchases, type PriceInfo, type OfferingPrice } from "../lib/purchases";
import { invalidateEntitlement, useEntitlement } from "../lib/useEntitlement";
import { SheetShell } from "./SheetShell";
import { PlanComparison } from "./PlanComparison";
import { Button } from "./ui/Button";

export type PaywallTrigger = "check_in" | "feature" | "settings";

interface Props {
  trigger: PaywallTrigger;
  onClose: () => void;
}


export function Paywall({ trigger, onClose }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const native = isNativeApp();
  const { billingEnabled } = useEntitlement();
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly");
  const [prices, setPrices] = useState<OfferingPrice[] | null>(null);
  const [priceMeta, setPriceMeta] = useState<Pick<PriceInfo, "source" | "storefront"> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    getPriceInfo().then((info) => {
      if (cancelled) return;
      setPrices(info.prices);
      setPriceMeta({ source: info.source, storefront: info.storefront });
    });
    return () => { cancelled = true; };
  }, [native]);

  // The live StoreKit price string, or null when none was fetchable. On
  // native we NEVER substitute a hardcoded currency: showing €39.99 on the
  // button while Apple's sheet charges the account's own storefront price
  // (e.g. $34.99 on a US account) is exactly the mismatch this fixes. Web is
  // marketing-only and cannot charge, so it keeps the € figure as a reference.
  const livePrice = (period: "yearly" | "monthly"): string | null =>
    prices?.find((p) => p.period === period)?.priceString
    ?? (native ? null : formatPrice(PRICING[period], locale));

  async function buy() {
    setBusy(true);
    try {
      if (native) {
        const result = await purchase(plan);
        if (result === "premium") {
          invalidateEntitlement();
          toast.success(t("paywall.welcome"));
          onClose();
        } else if (result === "pending") {
          // Ask to Buy or a bank approval: nothing is owed yet, and premium
          // arrives on its own once Apple settles it.
          toast.success(t("paywall.pending"));
          onClose();
        } else if (result === "failed") {
          toast.error(t("common.failed"));
        }
      } else {
        const { url } = await api.stripeCheckout(plan);
        window.location.href = url; // Stripe Checkout takes over
      }
    } catch {
      toast.error(t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    try {
      const r = await restorePurchases();
      if (r === "premium") {
        invalidateEntitlement();
        toast.success(t("paywall.restored"));
        onClose();
      } else if (r === "none") {
        toast.error(t("paywall.nothing_to_restore"));
      } else {
        toast.error(t("common.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell onClose={onClose}>
      {/* The title names the thing that was tapped. Arriving here from voice
          logging and being greeted by a generic "Go Premium" makes the user do
          the work of connecting the two. */}
      <div className="flex items-center gap-2 mt-4 mb-1">
        <Sparkles size={20} className="text-brand" aria-hidden="true" />
        <h3 className="text-lg font-semibold">{t(`paywall.title_${trigger}`)}</h3>
      </div>
      <p className="text-sm text-fg-muted mb-4">{t(`paywall.reason_${trigger}`)}</p>

      <div className="mb-5"><PlanComparison /></div>

      {/* No working checkout on this platform (web before Stripe exists):
          the table above still explains Premium, but the only honest CTA is
          none at all — a buy button whose sole outcome is an error teaches
          people the app is broken, not that the feature is paid. */}
      {!billingEnabled && (
        <p className="text-sm text-fg-muted text-center mb-4">{t("paywall.native_only")}</p>
      )}
      {billingEnabled && (<>
      <div className="space-y-2 mb-4" role="radiogroup" aria-label={t("paywall.title")}>
        {(["yearly", "monthly"] as const).map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={plan === p}
            onClick={() => setPlan(p)}
            className={`w-full flex items-center justify-between rounded-2xl px-4 py-3.5 border press ${
              plan === p ? "border-brand bg-brand/10" : "border-line bg-surface"
            }`}
          >
            <span className="text-sm font-medium">
              {t(p === "yearly" ? "paywall.yearly" : "paywall.monthly")}
              {p === "yearly" && (
                <span className="ml-2 text-sm text-brand">{t("paywall.best_value")}</span>
              )}
            </span>
            <span className="text-sm tabular-nums">
              {livePrice(p)
                ? <>{livePrice(p)}{t(p === "yearly" ? "paywall.per_year" : "paywall.per_month")}</>
                : <span className="text-fg-muted">{t("paywall.at_checkout")}</span>}
            </span>
          </button>
        ))}
      </div>

      <Button fullWidth onClick={buy} disabled={busy}>
        {busy ? t("common.loading") : t("paywall.start_trial")}
      </Button>
      <p className="text-sm text-fg-muted text-center mt-2">
        {livePrice(plan)
          ? t("paywall.trial_terms", { price: livePrice(plan)! })
          : t("paywall.trial_terms_generic")}
      </p>
      {native && !livePrice(plan) && (
        <p className="text-sm text-fg-faint text-center mt-1">{t("paywall.price_deferred")}</p>
      )}
      {native && livePrice(plan) && priceMeta?.source === "storekit" && priceMeta.storefront && (
        <p className="text-sm text-fg-faint text-center mt-1">{t("pro.price_source", { code: priceMeta.storefront })}</p>
      )}
      </>)}

      <div className="flex items-center justify-center gap-4 mt-4 mb-2 text-sm text-fg-muted">
        {native && (
          <button onClick={restore} disabled={busy} className="underline min-h-[44px]">
            {t("paywall.restore")}
          </button>
        )}
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          target="_blank" rel="noreferrer" className="underline"
        >{t("paywall.terms")}</a>
        <a href="https://caloly.com/privacy" target="_blank" rel="noreferrer" className="underline">
          {t("paywall.privacy")}
        </a>
      </div>
    </SheetShell>
  );
}
